import { legacy_createStore as createStore, applyMiddleware, compose } from 'redux';
import { createResponsiveStoreEnhancer } from 'ctm-redux-responsive';
import promise from 'redux-promise';
/* todo: this is only used in dev, but is being imported in prod builds */
import { createLogger } from 'redux-logger';
import Immutable from 'immutable';
import createSagaMiddleware from 'redux-saga';

import rootReducer from './reducers';
import { rootSaga } from './sagas/root_saga';

/**
 * Creates the Redux store for application state.
 * The store uses the redux-saga, promise and redux-logger middlewares and also sets up the HMR for reducer changes,
 * so that changed reducers can be hot-swapped into the store.
 * @param {Object} initialState - initial application state default {}
 * @returns {Object} store - the application store
 */
export const configureStore = (initialState = Immutable.Map()) => {
    const debugMode = process.env.EVEREST_DEPLOY_ENV?.trim() !== 'prod';
    const sagaMiddleware = createSagaMiddleware();
    const middlewares = getMiddleWares(debugMode, sagaMiddleware);
    const enhancers = getEnhancers(debugMode, middlewares);

    // Create a Redux store to hold the state for the application.
    const store = createStore(rootReducer, initialState, compose(...enhancers));

    // For hot reloading reducers
    if (module.hot) {
        // @todo: check this - I'm not sure that this will actually work, but we rarely need to hot reload reducers, so should be ok for now
        // Enable Webpack hot module replacement for reducers.
        // This allows us the modify reducers during development and the HMR will
        // reload the reducers into the store after a change has been saved.
        module.hot.accept('./reducers', () => {
            const nextReducer = require('./reducers').default; // eslint-disable-line global-require
            store.replaceReducer(nextReducer);
        });
    }

    sagaMiddleware.run(rootSaga);

    return store;
};

const LOGGER_IGNORED_ACTIONS = [
    // 'VALIDATE_AND_PROGRESS',
    // 'VALIDATE_PAGE',
    // 'TRACK_INITIAL_FORM',
    // 'SET_JOURNEY_PAYLOAD',
    // 'SET_LOGGING_IDENTIFY'
];

/**
 * Get the MiddleWare to be applied to the store.
 * @param {Boolean} debugMode - Debug mode flag.
 * @param  {...object} initialMiddleWares - Initial middleware to be included.
 * @returns {Array} - An array of middlewares.
 */
const getMiddleWares = (debugMode = false, ...initialMiddleWares) => {
    const middlewares = [
        ...initialMiddleWares,
        promise
    ];

    if (debugMode) {
        // ignore actions with large payloads
        const reduxLogger = createLogger({
            collapsed: true,
            predicate: (getState, action) => !LOGGER_IGNORED_ACTIONS.includes(action.type)
        });
        middlewares.push(reduxLogger);
    }

    return middlewares;
};

/**
 * Get the Enhancers to be applied to the store.
 * @param  {Boolean} debugMode - Debug mode flag.
 * @param  {Array} middlewares - The middlewares to be applied.
 * @returns {Array} - An array of enhancers.`
 */
const getEnhancers = (debugMode = false, middlewares) => {
    const enhancers = [
        // configure the responsive breakpoint listener
        // the performance mode flag ensures a redux action is only fired once the breakpoint is hit, instead of
        // every time the screen moves
        createResponsiveStoreEnhancer({ performanceMode: true }),

        // apply the application middleware
        applyMiddleware(...middlewares)
    ];

    if ((typeof window !== 'undefined') && debugMode) {
        if (window.__REDUX_DEVTOOLS_EXTENSION__) {
            // Enable DevTools only when rendering on client and during development.
            enhancers.push(window.__REDUX_DEVTOOLS_EXTENSION__());
        }
    }

    return enhancers;
};
